import type { Job } from '@mairistem/composants';
import { request } from '@mairistem/tools';

/** Job360 de l'analyse, avec la clé du fichier déposé (à repasser pour l'import). */
export interface JobAnalyseImportPes extends Job {
	cleFichier: string;
}

/** Pièce citée par une anomalie : de quoi la retrouver dans le flux. */
export interface PieceImportPes {
	fichier: string;
	bordereau: string;
	piece: string;
	objet: string | null;
	lignes: string[];
}

export type NiveauAnomalieImportPes =
	| 'bloquant'
	| 'avertissement'
	| 'information';

export interface AnomalieImportPes {
	niveau: NiveauAnomalieImportPes;
	code: string;
	message: string;
	/** occurrences de la cause (ex. tiers créés, fonctions sans imputation) */
	details: string[];
	pieces: PieceImportPes[];
}

/** Compte rendu de l'analyse ou de l'import (résultat JSON du Job360). */
export interface CompteRenduImportPes {
	importable: boolean;
	resume: Record<string, unknown>;
	anomalies: AnomalieImportPes[];
}

/** Flux PES déposé par un logiciel métier (paie, facturation…) dans cm_action, en attente d'import. */
export interface FluxPesEnAttente {
	identifiant: number;
	codeSrc: string | null;
	nom: string | null;
	/** Y-m-d */
	dateCreation: string | null;
	/** en octets */
	taille: number;
}

/** Fichier choisi sur le poste, ou flux en attente (son fichier reste côté serveur). */
export type SourceImportPes = File | FluxPesEnAttente;

const URL_IMPORT = '/api/finances/comptabilite/pes/import';

/** Flux en attente d'import pour l'exercice, du plus récent au plus ancien (sans leur fichier). */
export const getFluxPesEnAttente = (identifiantExercice: number) =>
	request.get<unknown, FluxPesEnAttente[]>(
		`${URL_IMPORT}/actions?identifiantExercice=${identifiantExercice}`,
	);

/**
 * Dépose un flux PES (XML ou zip), ou désigne un flux en attente, et lance son analyse : rien n'est écrit. Un flux en
 * attente part en JSON : le gateway ne relaie pas un multipart sans fichier (il envoie alors un corps vide).
 */
export const analyserImportPes = (
	source: SourceImportPes,
	identifiantExercice: number,
	identifiantService?: number,
) => {
	if (!(source instanceof File)) {
		return request.post<unknown, JobAnalyseImportPes>(`${URL_IMPORT}/analyse`, {
			identifiantAction: source.identifiant,
			identifiantExercice,
			identifiantService: identifiantService ?? null,
		});
	}
	const donnees = new FormData();
	donnees.append('fichier', source);
	donnees.append('identifiantExercice', String(identifiantExercice));
	if (identifiantService !== undefined) {
		donnees.append('identifiantService', String(identifiantService));
	}
	return request.post<FormData, JobAnalyseImportPes>(
		`${URL_IMPORT}/analyse`,
		donnees,
	);
};

/**
 * Lance l'import d'un fichier analysé (l'analyse est refaite, puis tout ou rien est écrit) ; un flux en attente est
 * marqué fait avec l'import.
 */
export const importerPes = (
	cleFichier: string,
	identifiantExercice: number,
	identifiantService?: number,
	identifiantAction?: number,
) =>
	request.post<unknown, Job>(URL_IMPORT, {
		cleFichier,
		identifiantExercice,
		identifiantService: identifiantService ?? null,
		identifiantAction: identifiantAction ?? null,
	});
